<template>
  <!-- Shown only while at least one active campaign is bidding below its market range.
       Every number here is real auction data - never show this banner on estimates we
       cannot back with the bid-depth replay (see docs/MARKET_CONTEXT_WIRING.md).

       The total COUNTS AUCTIONS, not calls: auctions the campaigns bid in and lost to a higher
       bid from another account (missedCalls). It does not leave out auctions no call arrived on:
       that gate was tried and reverted on 2026-09-04, since non-delivery depends on price
       (docs/MARKET_CONTEXT_DELIVERED_CALLS.md), and most auctions bring no call even when won at
       a high bid (I25). So inside the rx-market-intelligence rollout the banner says auctions, as
       Slice H's counts beside it do (I26; R38). From backend PR 2 the count also leaves out
       auctions the account's own campaigns won, so "from other buyers" holds (R44). With the flag
       off it keeps today's "winnable calls" wording. -->
  <b-alert
    :show="totalMissedCalls > 0"
    variant="warning"
    class="d-flex align-items-center justify-content-between"
    data-cy="alert-missed-calls"
  >
    <div>
      <font-awesome-icon
        :icon="['fas', 'exclamation-triangle']"
        class="mr-2"
      />
      <strong>{{ headline }}</strong>
      <span class="text-muted">
        across {{ campaignsBelowMarket }} {{ campaignsBelowMarket === 1 ? 'campaign' : 'campaigns' }} bidding below market range.
      </span>
    </div>

    <b-button
      v-b-tooltip.hover
      variant="warning"
      size="sm"
      class="text-nowrap ml-3"
      :title="reviewButtonTooltip"
      data-cy="button-review-missed-calls"
      @click="$emit('review')"
    >
      Review below-market bids
    </b-button>
  </b-alert>
</template>

<script>
export default {
  name: 'MissedCallsBanner',

  props: {
    totalMissedCalls: {
      type: Number,
      default: 0
    },

    campaignsBelowMarket: {
      type: Number,
      default: 0
    },

    // The window the missed-call totals were measured over, as the `long` form of
    // formatMarketWindow (e.g. 'last 24 hours', 'last 7 days'). NOT a fixed 24 hours: on
    // Mondays this banner's total covers 7 days and so runs roughly 7x higher, which is the
    // whole reason it has to be stated rather than assumed
    windowLabel: {
      type: String,
      default: ''
    },

    // Inside the rx-market-intelligence rollout: the copy says auctions (I26; R38; R41)
    auctionsWording: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    // Rendered bare while the window is unknown. The banner only shows once there are missed
    // calls to report, so in practice the fetch has resolved and the label is present - this
    // is what stops a half-written sentence if it ever isn't
    windowSuffix() {
      return this.windowLabel ? ` in the ${this.windowLabel}` : ''
    },

    headline() {
      const total = this.totalMissedCalls.toLocaleString()

      if (!this.auctionsWording) {
        return `${total} winnable calls went to other buyers${this.windowSuffix}`
      }

      return this.totalMissedCalls === 1
        ? `${total} auction went to a higher bid from another buyer${this.windowSuffix}`
        : `${total} auctions went to higher bids from other buyers${this.windowSuffix}`
    },

    reviewButtonTooltip() {
      return `Sorts the table by missed ${this.auctionsWording ? 'auctions' : 'calls'} so the biggest gaps are on top`
    }
  }
}
</script>

<style lang="scss" scoped></style>
